import React, { useState, useEffect } from 'react';
import { CreditCard, ShieldCheck, Sparkles } from 'lucide-react';
import StripeInlinePayment from './StripeInlinePayment';

interface StripeButtonProps {
  videoId: string;
  jobId?: string;
  amount: string;   // "0.895" (1 minute example)
  currency: string; // "USD"
  autoShow?: boolean; // Auto-show form when true
  onPaymentComplete: (paymentId: string) => void;
}

export default function StripeButton({
                                       videoId,
                                       jobId,
                                       amount,
                                       currency,
                                       autoShow = false,
                                       onPaymentComplete,
                                     }: StripeButtonProps) {
  const [show, setShow] = useState(autoShow);

  // Auto-show form when autoShow prop changes
  useEffect(() => {
    if (autoShow && !show) {
      setShow(true);
    }
  }, [autoShow, show]);

  if (show) {
    return (
        <StripeInlinePayment
            videoId={videoId}
            jobId={jobId}
            amount={amount}
            currency={currency}
            onPaymentComplete={(id) => {
              setShow(false);
              onPaymentComplete(id);
            }}
            onCancel={() => setShow(false)}
        />
    );
  }

  const formattedTotal = (() => {
    const n = Number.parseFloat(amount || '0');
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
    } catch {
      return `${currency} ${n.toFixed(2)}`;
    }
  })();

  return (
      <button
          type="button"
          onClick={() => setShow(true)}
          aria-expanded={show}
          aria-controls="stripe-inline-payment"
          className="group w-full text-left"
      >
        <div className="relative overflow-hidden rounded-2xl  bg-gradient-to-br from-white to-slate-50 shadow-sm transition-all duration-300 group-hover:shadow-xl" data-payment-section-stripe={true}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Powered by Stripe</p>
                <h3 className="text-sm font-semibold text-slate-900">Secure Payment</h3>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              256-bit SSL
            </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
              PCI DSS
            </span>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 pb-6 pt-7 text-center">
            <p className="text-xs tracking-wide text-slate-500">Total</p>
            <div className="mt-1 text-2xl font-bold text-slate-900">{formattedTotal}</div>

           
            {/* CTA */}
            <div className="mt-6 inline-flex w-full max-w-2xl items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 text-white shadow-lg transition-all duration-300 group-hover:from-indigo-700 group-hover:to-blue-700">
              <CreditCard className="h-5 w-5" />
              <span className="text-base font-semibold">Pay with Card — {formattedTotal}</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                />
              </svg>
            </div>

            <p className="mt-3 text-center text-xs text-slate-500">
              Fast checkout. Bank-level security. We don’t store your card details.
            </p>
          </div>
        </div>
      </button>
  );
}
